/**
 * m3 echo Frontend - Password Change Page
 *
 * Standalone page for password change (for users with must_change_password flag).
 * This page handles its own authentication check to avoid redirect loops.
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '../stores/useAuthStore';
import { authService } from '../services/auth';
import { PasswordChangeDialog } from '../components/PasswordChangeDialog';

export const PasswordChangePage: React.FC = () => {
  const {
    isAuthenticated,
    isInitialized,
    setAuth,
    setInitialized,
    clearAuth,
    user,
    maintenanceMode,
  } = useAuthStore();

  const [isChecking, setIsChecking] = useState(true);

  // On mount, try to restore session from HttpOnly cookie
  useEffect(() => {
    async function checkSession() {
      // Skip if already initialized
      if (isInitialized) {
        setIsChecking(false);
        return;
      }

      try {
        // Try to refresh using the HttpOnly cookie
        const response = await authService.checkSession();

        if (response) {
          console.log('[PasswordChangePage] Session restored from cookie');
          setAuth(response);
        } else {
          console.log('[PasswordChangePage] No valid session found');
          clearAuth();
        }
      } catch (error) {
        console.log('[PasswordChangePage] Session check failed:', error);
        clearAuth();
      } finally {
        setInitialized(true);
        setIsChecking(false);
      }
    }

    checkSession();
  }, [isInitialized, setAuth, setInitialized, clearAuth]);

  // Show loading spinner while checking session
  if (isChecking || !isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not logged in, redirect to login page
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // During maintenance, non-admin users go to / (ProtectedRoute shows maintenance page)
  if (maintenanceMode && !user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  // User doesn't need to change password, redirect to home
  if (!user?.must_change_password) {
    return <Navigate to="/" replace />;
  }

  // Show password change dialog
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
      }}
    >
      <PasswordChangeDialog
        isOpen={true}
        onClose={() => {
          // After successful password change, redirect to home
          window.location.href = '/';
        }}
        isRequired={true}
      />
    </Box>
  );
};

export default PasswordChangePage;
